import React, { useCallback, useMemo, useState } from 'react';
import { FolderOpen } from 'lucide-react';
import { SectionLayout } from '@/components/section-layout';
import { FloatingAlert } from '@/components/ui/floating-alert';
import { StandardButton } from '@/styles/StandardButtons';
import { useProject } from '@/components/ProjectSelector';
import { useUser } from '@/contexts/UserContext';
import { trackFileUploaded, trackFileDeleted, trackFolderCreated, trackDriveNavigation } from '@/lib/posthog-events';
import { useDrive } from './hooks/useDrive';
import { DriveToolbar } from './components/DriveToolbar';
import { BreadcrumbNavigation } from './components/BreadcrumbNavigation';
import { DriveList } from './components/DriveList';
import { DriveThumbnails } from './components/DriveThumbnails';
import { AddUrlDialog, MoveDialog, NewFolderDialog, RenameDialog } from './components/DriveDialogs';
import type { DriveItemActions } from './components/DriveItemParts';
import { downloadFile, openFile, openLink } from './lib/drive-items';
import type { DriveItem } from './types';

type Dialog = { kind: 'folder' } | { kind: 'url' } | { kind: 'rename'; item: DriveItem } | { kind: 'move'; ids: string[] };

export function DrivePage() {
  const { selectedProject } = useProject();
  const { companyId, user, userRole } = useUser();
  const projectId = selectedProject && selectedProject !== 'all' ? selectedProject : '';
  // a client / vendor only reads what its shared folder holds (the server and the database refuse everything else)
  const readOnly = userRole === 'client' || userRole === 'vendor';
  const drive = useDrive(projectId, companyId || '');
  const { items, selectedItems, currentFolderId, breadcrumbs, notify } = drive;
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const closeDialog = useCallback(() => setDialog(null), []);

  const selected = useMemo(() => items.filter((i) => selectedItems.includes(i.id)), [items, selectedItems]);

  const navigate = useCallback((folderId: string | null) => {
    trackDriveNavigation({ from_folder_id: currentFolderId, to_folder_id: folderId, project_id: projectId, company_id: companyId, navigated_by: user?.id });
    drive.navigateToFolder(folderId);
  }, [drive, currentFolderId, projectId, companyId, user?.id]);

  const fail = useCallback((title: string, e: unknown) => notify({ title, description: e instanceof Error ? e.message : undefined, variant: 'destructive' }), [notify]);

  const deleteItems = useCallback(async (list: DriveItem[]) => {
    const what = list.length === 1 ? `"${list[0].name}"` : `${list.length} items`;
    const hasFolder = list.some((i) => i.type === 'folder');
    if (!window.confirm(`Are you sure you want to delete ${what}?${hasFolder ? ' A folder is deleted with everything inside it.' : ''} This action cannot be undone.`)) return;
    try {
      await drive.deleteItems(list.map((i) => i.id));
      list.forEach((item) => trackFileDeleted({ item_id: item.id, item_name: item.name, item_type: item.type, project_id: projectId, folder_id: currentFolderId, company_id: companyId, deleted_by: user?.id }));
    } catch {
      /* the hook already told the user */
    }
  }, [drive, projectId, currentFolderId, companyId, user?.id]);

  const actions: DriveItemActions = useMemo(() => ({
    onOpen: (item) => {
      if (item.type === 'folder') navigate(item.id);
      else if (item.type === 'url') {
        if (!openLink(item.url)) notify({ title: 'This link cannot be opened', description: 'Only http:// and https:// addresses are opened.', variant: 'destructive' });
      } else openFile(item).catch((e) => fail('Could not open the file', e));
    },
    onRename: (item) => setDialog({ kind: 'rename', item }),
    onDownload: (item) => downloadFile(item).catch((e) => fail('Could not download the file', e)),
    onMove: (item) => setDialog({ kind: 'move', ids: [item.id] }),
    onDelete: (item) => deleteItems([item]),
    readOnly,
  }), [navigate, notify, fail, deleteItems, readOnly]);

  if (!projectId) {
    return (
      <SectionLayout title="Drive">
        <div className="text-center p-8 font-subheading-text">Please select a specific project to access the drive</div>
      </SectionLayout>
    );
  }

  const parentCrumb = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;
  const body = drive.error ? (
    <div className="text-center p-8 space-y-3">
      <div className="text-red-600">Error: {drive.error}</div>
      <StandardButton customVariant="white" onClick={() => drive.navigateToFolder(currentFolderId)}>Retry</StandardButton>
    </div>
  ) : drive.isLoading && items.length === 0 ? (
    <div className="p-12 text-center font-subheading-text">Loading...</div>
  ) : items.length === 0 ? (
    <div className="flex flex-col items-center justify-center p-12 text-center">
      <FolderOpen className="w-10 h-10 text-gray-300 mb-3" />
      <h3 className="font-heading-text mb-1">{readOnly ? 'No shared files' : 'This folder is empty'}</h3>
      <p className="font-subheading-text">{readOnly ? 'Nothing has been shared with you in this project yet' : 'Create a folder, upload files or add a link'}</p>
    </div>
  ) : drive.viewMode === 'grid' ? (
    <DriveThumbnails items={items} selectedItems={selectedItems} onSelect={drive.selectItem} onSelectAll={drive.selectAllItems} actions={actions} />
  ) : (
    <DriveList items={items} selectedItems={selectedItems} onSelect={drive.selectItem} onSelectAll={drive.selectAllItems} actions={actions} />
  );

  return (
    <SectionLayout title="Drive">
      <div className="space-y-4">
        <DriveToolbar
          readOnly={readOnly}
          canGoUp={Boolean(parentCrumb)}
          onGoUp={() => parentCrumb && navigate(parentCrumb.folderId)}
          onNewFolder={() => setDialog({ kind: 'folder' })}
          onAddUrl={() => setDialog({ kind: 'url' })}
          onUploadFiles={async (files) => {
            const { uploaded } = await drive.uploadFiles(files);
            if (uploaded > 0) files.forEach((file) => trackFileUploaded({ file_name: file.name, file_size: file.size, file_type: file.type, project_id: projectId, folder_id: currentFolderId, company_id: companyId, created_by: user?.id }));
          }}
          selected={selected}
          onRename={(item) => setDialog({ kind: 'rename', item })}
          onDownload={drive.downloadItems}
          onMove={(list) => setDialog({ kind: 'move', ids: list.map((i) => i.id) })}
          onDelete={deleteItems}
          onClearSelection={drive.clearSelection}
          viewMode={drive.viewMode}
          onViewChange={drive.setViewMode}
        />
        <BreadcrumbNavigation breadcrumbs={breadcrumbs} onNavigate={navigate} />
        <div className="w-full">{body}</div>
      </div>

      {dialog?.kind === 'folder' && (
        <NewFolderDialog
          onCancel={closeDialog}
          onSave={async (name) => {
            await drive.createFolder({ name });
            trackFolderCreated({ folder_name: name, project_id: projectId, parent_folder_id: currentFolderId, company_id: companyId, created_by: user?.id });
          }}
        />
      )}
      {dialog?.kind === 'url' && <AddUrlDialog onCancel={closeDialog} onSave={(name, url) => drive.createUrl({ name, url })} />}
      {dialog?.kind === 'rename' && (
        <RenameDialog item={dialog.item} onCancel={closeDialog} onSave={(name, url) => drive.renameItem({ id: dialog.item.id, newName: name, newUrl: url })} />
      )}
      {dialog?.kind === 'move' && <MoveDialog ids={dialog.ids} loadFolders={drive.loadFolders} onCancel={closeDialog} onSave={(parentId) => drive.moveItems(dialog.ids, parentId)} />}
      {drive.notice && <FloatingAlert {...drive.notice} onDismiss={() => notify(null)} />}
    </SectionLayout>
  );
}
